import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  addVideoQRVideo,
  createVideoQR,
  deleteVideoQRVideo,
  updateVideoQR,
  updateVideoQRVideo,
} from '~/api/microsites/video';
import { qrcodeAPIService } from '~/api/qrcode';
import BuilderSecondStepLayout from '~/components/builder/BuilderSecondStepLayout';
import Loader from '~/components/ui/Loader';
import { FONTS, PREVIEW_BAR_VIEW_TYPE } from '~/constants/common';
import {
  QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME,
  QR_CODE_ID_PARAM_NAME,
  QRCODE_DYNAMIC_MICROSITE_TYPE,
  VIDEO_QR_ID_PARAM_NAME,
} from '~/constants/qr-code';
import { qrCodeKeys } from '~/constants/queryKeys';
import { VIDEO_TEMPLATE_COLORS } from '~/constants/video';
import usePrefillQrCodeData from '~/hooks/usePrefillQrCodeData';
import useQrCodeData from '~/hooks/useQrCodeData';
import useVideoQrData from '~/hooks/useVideoQrData';
import CreateQrDesignQRStepV2 from '~/pages/CreateQR/components/CreateQrDesignQRStepV2';
import { getVideoQRSchema, VideoQRSchema } from '~/pages/CreateQR/sub-components/videoTemplate/schema';
import VideoTemplateAppearance from '~/pages/CreateQR/sub-components/videoTemplate/VideoTemplateAppearance';
import VideoTemplateBasicInformation from '~/pages/CreateQR/sub-components/videoTemplate/VideoTemplateBasicInformation';
import useBuilderStore from '~/store/useBuilderStore';
import useVideoTemplateStore from '~/store/useVideoTemplateStore';
import { DynamicQrCodeData, UpdateQrCodeDataPayload } from '~/types/qrCode';
import { CreateVideoQrPayload, UpdateVideoQrPayload } from '~/types/video';
import { getUpdateDynamicQrCodePayload } from '~/utils/builder';

export default function VideoTemplate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const videoQrId = searchParams.get(VIDEO_QR_ID_PARAM_NAME);
  const qrCodeId = searchParams.get(QR_CODE_ID_PARAM_NAME);
  const isEditMode = searchParams.get(QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME);

  const { data: qrCodeData, isLoading: isLoadingQrCodeData } = useQrCodeData<DynamicQrCodeData>({ qrCodeId });

  const videoQRSchema = getVideoQRSchema({ t, qrCodeUri: qrCodeData?.uri });

  const styles = useStyles();
  const { setActiveStep, activeStep, setPreviewViewType } = useBuilderStore();
  const { videoIdsToDelete, resetVideoIdsToDelete } = useVideoTemplateStore();

  const { mutateAsync: createVideoQRMutateAsync, isPending: isPendingCreateVideoQR } = useMutation({
    mutationFn: createVideoQR,
    onSuccess: (response) => {
      searchParams.set(VIDEO_QR_ID_PARAM_NAME, String(response.id));
      setSearchParams(searchParams);
      setActiveStep(2);
      setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.QRCODE);
    },
  });

  const { mutateAsync: updateVideoQRMutateAsync, isPending: isPendingUpdateVideoQR } = useMutation({
    mutationFn: updateVideoQR,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qrCodeKeys.couponQrData });
      setActiveStep(2);
      setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.QRCODE);
    },
  });

  const { mutateAsync: updateQrCodeMutateAsync, isPending: isPendingUpdateQrCode } = useMutation({
    mutationFn: (body: UpdateQrCodeDataPayload) => qrcodeAPIService.updateQrCode(body, qrCodeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qrCodeKeys.qrCodeData });
    },
  });

  const { mutateAsync: deleteVideoItemMutateAsync, isPending: isPendingDeleteVideoItem } = useMutation({
    mutationFn: deleteVideoQRVideo,
  });

  const { mutateAsync: addVideoItemMutateAsync, isPending: isPendingAddVideoItem } = useMutation({
    mutationFn: addVideoQRVideo,
  });

  const { mutateAsync: updateVideoItemMutateAsync, isPending: isPendingUpdateVideoItem } = useMutation({
    mutationFn: updateVideoQRVideo,
  });

  const { data: videoQrData, isLoading: isLoadingVideoQrData } = useVideoQrData({ videoId: videoQrId });

  const isVideoData = !!videoQrData;
  const isQRCodeData = !!qrCodeData;

  const form = useForm<VideoQRSchema>({
    defaultValues: {
      qrCodeName: '',
      appearance: {
        design: {
          primary: VIDEO_TEMPLATE_COLORS[0],
        },
        fonts: {
          title: FONTS[3].value,
          text: FONTS[3].value,
          titleColor: '#FFFFFF',
          textsColor: '#FFFFFF',
        },
      },
      basicInfo: {
        video: {
          videoUrl: '',
          showVideoDirectly: false,
          files: [],
        },
        information: {
          company: 'Worker John',
          videoTitle: 'How to Build Your House',
          description:
            'Learn step-by-step how to design, plan, and build I your dream home with our comprehensive construction course.',
          buttonText: 'View More',
          buttonURL: 'https://www.example.com/',
        },
      },
      settings: {
        isAutoGeneratedUrl: true,
        isScanLimitsEnabled: false,
      },
    },
    resolver: zodResolver(videoQRSchema),
  });

  useEffect(() => {
    if (qrCodeData?.uri !== undefined) {
      form.setValue('settings.qrCodeUrl', qrCodeData?.uri);
    }

    if (qrCodeData?.isAutogeneratedUrl !== undefined) {
      form.setValue('settings.isAutoGeneratedUrl', qrCodeData.isAutogeneratedUrl);
    }
  }, [qrCodeData?.uri, qrCodeData?.isAutogeneratedUrl]);

  useEffect(() => {
    if (isVideoData && isEditMode) {
      // Appearance - Design
      form.setValue('appearance.design.primary', videoQrData.design.primary);
      // Appearance - Fonts
      form.setValue('appearance.fonts.title', videoQrData.fonts.title);
      form.setValue('appearance.fonts.text', videoQrData.fonts.text);
      form.setValue('appearance.fonts.titleColor', videoQrData.fontsColors.title);
      form.setValue('appearance.fonts.textsColor', videoQrData.fontsColors.text);
      // Basic info - information
      form.setValue('basicInfo.information.company', videoQrData.company);
      form.setValue('basicInfo.information.videoTitle', videoQrData.title);
      form.setValue('basicInfo.information.description', videoQrData.description);
      if (videoQrData.ctaText) {
        form.setValue('basicInfo.information.buttonText', videoQrData.ctaText);
      }
      if (videoQrData.ctaUrl) {
        form.setValue('basicInfo.information.buttonURL', videoQrData.ctaUrl);
      }
      // Basic info - video
      form.setValue('basicInfo.video.showVideoDirectly', videoQrData.highlightFirstVideo);
      form.setValue(
        'basicInfo.video.files',
        videoQrData.videos.map((video) => {
          return {
            id: video.id,
            sourceType: video.sourceType,
            sourceUrl: video.sourceUrl ?? '',
            thumbnail: video.thumbnail ?? '',
            displayOrder: video.displayOrder,
            description: video.description ?? '',
            videoFile: video.videoFile,
          };
        }),
      );
    }
  }, [isVideoData, isEditMode]);

  usePrefillQrCodeData({ qrCodeData, isQRCodeData, setValue: form.setValue });

  const handleSubmit = form.handleSubmit(async (values) => {
    const qrCodeDataPayload = getUpdateDynamicQrCodePayload({
      values: {
        settings: values.settings,
        qrCodeName: values.qrCodeName,
        qrCodeFolder: values.qrCodeFolder,
        passwordProtection: values.passwordProtection,
      },
      qrCodeData,
    });

    const videosToSend = values.basicInfo.video.files
      .filter(({ videoFileId, sourceType, id }) => (!!videoFileId || sourceType === 'YOUTUBE') && !id)
      .map((video) => {
        if (video.sourceType === 'YOUTUBE') {
          return {
            description: video.description,
            displayOrder: video.displayOrder,
            sourceType: video.sourceType,
            sourceUrl: video.sourceUrl,
          };
        }

        return {
          videoFileId: video.videoFileId,
          description: video.description,
          displayOrder: video.displayOrder,
          sourceType: video.sourceType,
        };
      });

    const videosToUpdate = values.basicInfo.video.files.filter(({ displayOrder, description, id }, index) => {
      if (id) {
        const videoDataFromDb = videoQrData?.videos?.find((dbVideo) => dbVideo.id === id);
        const isDescriptionChanged = !!videoDataFromDb && videoDataFromDb.description !== description;

        return displayOrder !== index || isDescriptionChanged;
      }

      return false;
    });

    if (videoQrData && videoQrId) {
      const payload: UpdateVideoQrPayload = {
        design: values.appearance.design,
        fonts: {
          title: values.appearance.fonts.title,
          text: values.appearance.fonts.text,
        },
        fontsColors: {
          title: values.appearance.fonts.titleColor,
          text: values.appearance.fonts.textsColor,
        },
        company: values.basicInfo.information.company,
        description: values.basicInfo.information.description,
        title: values.basicInfo.information.videoTitle,
        ctaText: values.basicInfo.information.buttonText,
        ctaUrl: values.basicInfo.information.buttonURL,
        highlightFirstVideo: values.basicInfo.video.files.length > 1 ? false : values.basicInfo.video.showVideoDirectly,
      };

      if (videosToSend.length) {
        await Promise.all(videosToSend.map((video) => addVideoItemMutateAsync({ videoQrId, video })));
      }

      if (videoIdsToDelete.length) {
        await Promise.all(videoIdsToDelete.map((videoId) => deleteVideoItemMutateAsync({ videoQrId, videoId })));
        resetVideoIdsToDelete();
      }

      if (videosToUpdate.length) {
        await Promise.all(
          videosToUpdate.map((video, index) =>
            updateVideoItemMutateAsync({
              videoQrId,
              videoId: video.id,
              payload: { description: video.description, displayOrder: index },
            }),
          ),
        );
      }

      await updateVideoQRMutateAsync({ payload, id: videoQrId });
      if (Object.keys(qrCodeDataPayload).length) {
        //  must be called after update of video qr
        await updateQrCodeMutateAsync(qrCodeDataPayload);
      }
    } else {
      const payload: CreateVideoQrPayload = {
        qrCodeId: Number(qrCodeId),
        design: values.appearance.design,
        fonts: {
          title: values.appearance.fonts.title,
          text: values.appearance.fonts.text,
        },
        fontsColors: {
          title: values.appearance.fonts.titleColor,
          text: values.appearance.fonts.textsColor,
        },
        videos: videosToSend,
        company: values.basicInfo.information.company,
        description: values.basicInfo.information.description,
        title: values.basicInfo.information.videoTitle,
        ctaText: values.basicInfo.information.buttonText,
        ctaUrl: values.basicInfo.information.buttonURL,
        highlightFirstVideo: values.basicInfo.video.files.length > 1 ? false : values.basicInfo.video.showVideoDirectly,
      };

      await createVideoQRMutateAsync(payload);

      if (Object.keys(qrCodeDataPayload).length) {
        //  must be called after update of video qr
        await updateQrCodeMutateAsync(qrCodeDataPayload);
      }
    }
  });

  const showDesignStep = activeStep === 2 || activeStep === 3;

  return (
    <FormProvider {...form}>
      {activeStep === 1 && (
        <>
          {isEditMode && (isLoadingVideoQrData || isLoadingQrCodeData) ? (
            <Box sx={styles.loaderWrapper}>
              <Loader />
            </Box>
          ) : (
            <BuilderSecondStepLayout
              onNext={handleSubmit}
              qrType={QRCODE_DYNAMIC_MICROSITE_TYPE.COUPON}
              isNextProcessing={
                isPendingUpdateVideoQR ||
                isPendingCreateVideoQR ||
                isPendingUpdateQrCode ||
                isPendingDeleteVideoItem ||
                isPendingAddVideoItem ||
                isPendingUpdateVideoItem
              }
            >
              <VideoTemplateAppearance />
              <VideoTemplateBasicInformation />
            </BuilderSecondStepLayout>
          )}
        </>
      )}
      {showDesignStep && <CreateQrDesignQRStepV2 />}
    </FormProvider>
  );
}

function useStyles() {
  return {
    loaderWrapper: css`
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    `,
  };
}
